import { useCallback, useEffect } from "react";
import {
    Box,
    Card,
    Stack,
    FormLabel,
    Container,
    Typography,
    FormControl,
} from "@mui/material";
import { PersonAdd } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Formik, Form, FormikHelpers } from "formik";
import { Link, useNavigate, useBeforeUnload } from "react-router-dom";
import {
    FormInput,
    FormCheckbox,
    FormDatePicker,
    PhoneNumberInput,
    PlacesAutocomplete,
    GoogleMap,
} from "src/components";
import { useTitle } from "react-use";
import {
    authAPI,
    authSelector,
    logout,
    navigatorSelector,
} from "../../redux/authSlice";
import { enqueueSnackbar } from "notistack";
import { getSchema } from "../../schema";
import {
    alertIfMissingFields,
    isFetchBaseQueryError,
    isValidCoord,
} from "src/utils/helpers";
import { useAppSelector } from "src/hooks";
import {
    getInitialValues,
    formatNavigatorAccount,
    formatParaplanAccount,
} from "./helpers";
import { PassioService } from "src/types";
import { useDispatch } from "react-redux";
import { SignupFormData } from "./types";

const Signup = () => {
    useTitle("Connect Signup");
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [signup, { isLoading }] = authAPI.useSignupMutation();
    const { serviceUrl, config } = useAppSelector(authSelector);
    const isNavigator = useAppSelector(navigatorSelector);
    const agency = config?.agencyId ?? "";
    const params = new URLSearchParams(window.location.search);
    const email = params.get("email") || "";
    const schema = getSchema(isNavigator, email);
    const initialValues = getInitialValues(isNavigator, email);

    function handleRedirect() {
        dispatch(logout());
        // reset location state to prevent auto redirects after login
        navigate(
            {
                pathname: "/login",
                search: !isNavigator
                    ? `service=${PassioService.paraplan}`
                    : undefined,
            },
            { replace: true, state: {} }
        );
    }

    async function handleSignup(
        values: SignupFormData,
        helpers: FormikHelpers<any>
    ) {
        try {
            if (isNavigator) {
                const account = formatNavigatorAccount(values, agency);
                await signup({ account }).unwrap();
            } else {
                const account = formatParaplanAccount(values);
                await signup({ agency, account }).unwrap();
            }
            enqueueSnackbar("Account created successfully", {
                variant: "success",
            });

            handleRedirect();
        } catch (error) {
            const defaultMsg = "Sorry, an error occured. Please try again";
            const message = isFetchBaseQueryError(error)
                ? (error.data as any)?.err ||
                  (error.data as any)?.message ||
                  defaultMsg
                : defaultMsg;
            if (message.includes("email")) {
                helpers.setFieldError("email", message);
            }
            enqueueSnackbar(message, { variant: "error" });
        }
    }

    /**
     * only proceed if service url is present
     * if agency name was passed to / or /login and /signup paths,
     * serviceURL will be available after the agency config is fetched
     */

    useEffect(() => {
        if ((isNavigator && !agency) || (!isNavigator && !serviceUrl)) {
            navigate("/login");
        }
    }, [serviceUrl, agency, navigate, isNavigator]);

    // always clear cache when the user leaves this page
    useBeforeUnload(
        useCallback(() => {
            dispatch(logout());
        }, [dispatch])
    );

    return (
        <Container maxWidth="lg" sx={{ p: 1.5 }}>
            <Stack
                direction={{ sm: "column", md: "row" }}
                sx={{
                    my: 4,
                    alignItems: "center",
                    justifyContent: { xs: "center", md: "space-between" },
                }}
            >
                <Stack direction="row" spacing={1} alignItems="center">
                    <PersonAdd fontSize="large" />
                    <Typography variant="h4" component="h1">
                        Connect Signup
                    </Typography>
                </Stack>

                <Typography my={2}>
                    Already have an account?{" "}
                    <Link
                        to={{
                            pathname: "/login",
                            search: !isNavigator
                                ? `service=${PassioService.paraplan}`
                                : undefined,
                        }}
                        onClick={() => dispatch(logout())}
                    >
                        Login
                    </Link>
                </Typography>
            </Stack>

            <Formik
                initialValues={initialValues}
                validationSchema={schema}
                onSubmit={handleSignup}
            >
                {({ values, errors }) => (
                    <Form>
                        <Stack spacing={4}>
                            {/* Agency Profile */}
                            <Card variant="elevation" sx={{ p: 1.5 }}>
                                <FormControl
                                    component="fieldset"
                                    sx={{ mb: 4, display: "flex", gap: 2 }}
                                >
                                    <FormLabel
                                        component="legend"
                                        sx={{
                                            mb: 2,
                                            fontSize: 20,
                                            color: "primary.main",
                                        }}
                                    >
                                        Account Details
                                    </FormLabel>

                                    <Stack
                                        spacing={2}
                                        direction={{ xs: "column", md: "row" }}
                                    >
                                        <FormInput
                                            name="firstName"
                                            label="First Name"
                                            sx={{ flexGrow: 1 }}
                                        />
                                        <FormInput
                                            name="lastName"
                                            label="Last Name"
                                            sx={{ flexGrow: 1 }}
                                        />
                                    </Stack>

                                    <Stack
                                        spacing={2}
                                        direction={{
                                            xs: "column",
                                            md: "row",
                                        }}
                                    >
                                        <PhoneNumberInput
                                            type="tel"
                                            name="phone"
                                            label="Phone"
                                            sx={{ flexGrow: 1 }}
                                            autoComplete="tel"
                                        />

                                        <FormInput
                                            type="email"
                                            name="email"
                                            label="Email"
                                            sx={{ flexGrow: 1 }}
                                            disabled={Boolean(email)}
                                        />

                                        <FormDatePicker
                                            format="MM/dd/yyyy"
                                            name="birthdate"
                                            label={`Birthday ${
                                                isNavigator ? "(Optional)" : ""
                                            }`}
                                            sx={{ flexGrow: 1 }}
                                            disableError
                                        />
                                    </Stack>
                                    {isNavigator && (
                                        <Stack direction="row" spacing={4}>
                                            <FormInput
                                                label="Validation Code"
                                                name="validationCode"
                                                helperText="This code may be required to access certain services"
                                                sx={{ flex: 1 }}
                                            />
                                        </Stack>
                                    )}
                                </FormControl>
                            </Card>

                            {/* Password, hidden for SSO created accounts */}
                            {!email && (
                                <Card variant="elevation" sx={{ p: 1.5 }}>
                                    <FormControl
                                        component="fieldset"
                                        sx={{ mb: 4, display: "flex", gap: 2 }}
                                    >
                                        <FormLabel
                                            component="legend"
                                            sx={{
                                                mb: 2,
                                                fontSize: 20,
                                                color: "primary.main",
                                            }}
                                        >
                                            Password
                                        </FormLabel>

                                        <Stack spacing={1}>
                                            <FormInput
                                                type="password"
                                                name="password"
                                                label="Password"
                                                sx={{ flexGrow: 1 }}
                                            />
                                            <FormInput
                                                type="password"
                                                name="confirmPassword"
                                                label="confirmPassword"
                                                sx={{ flexGrow: 1 }}
                                            />
                                        </Stack>
                                    </FormControl>
                                </Card>
                            )}

                            {/* Accessibility */}
                            <Card variant="elevation" sx={{ p: 1.5 }}>
                                <FormControl
                                    component="fieldset"
                                    sx={{ mb: 4, display: "flex", gap: 2 }}
                                >
                                    <FormLabel
                                        component="legend"
                                        sx={{
                                            mb: 2,
                                            fontSize: 20,
                                            color: "primary.main",
                                        }}
                                    >
                                        Accessibility
                                    </FormLabel>

                                    <Stack
                                        spacing={1}
                                        direction={{ xs: "column", md: "row" }}
                                    >
                                        <FormCheckbox
                                            name="accessibility.hasPCA"
                                            label="I travel with a Personal Care Attendant"
                                            sx={{ flexGrow: 1 }}
                                        />

                                        <FormCheckbox
                                            name="accessibility.usesWheelchair"
                                            label="I would like an ADA accessible vehicle"
                                            sx={{ flexGrow: 1 }}
                                        />

                                        <FormInput
                                            name="accessibility.wheelChairType"
                                            label="Wheelchair Type"
                                            sx={{ flexGrow: 1 }}
                                        />
                                    </Stack>
                                </FormControl>
                            </Card>

                            {/* Home Address */}
                            <Card variant="elevation" sx={{ p: 1.5 }}>
                                <FormControl
                                    component="fieldset"
                                    sx={{ mb: 4, display: "flex", gap: 2 }}
                                >
                                    <FormLabel
                                        component="legend"
                                        sx={{
                                            mb: 2,
                                            fontSize: 20,
                                            color: "primary.main",
                                        }}
                                    >
                                        Home Address{" "}
                                        {isNavigator && "(Optional)"}
                                    </FormLabel>

                                    <Box>
                                        <PlacesAutocomplete
                                            label="Address"
                                            name="address"
                                            validationKey="address1"
                                        />

                                        {isValidCoord({
                                            lat: values.address?.latitude,
                                            lng: values.address?.longitude,
                                        }) && (
                                            <GoogleMap
                                                pickUpCoords={{
                                                    lat:
                                                        values?.address
                                                            ?.latitude || 0,
                                                    lng:
                                                        values?.address
                                                            ?.longitude || 0,
                                                }}
                                                sx={{
                                                    height: 450,
                                                    width: "100%",
                                                    my: 2,
                                                }}
                                            />
                                        )}
                                    </Box>
                                </FormControl>
                            </Card>
                        </Stack>

                        <Box maxWidth="sm" mx="auto" my={4}>
                            <LoadingButton
                                size="large"
                                fullWidth
                                type="submit"
                                variant="contained"
                                sx={{ py: 1.25 }}
                                loading={isLoading}
                                onClick={() => alertIfMissingFields(errors)}
                            >
                                Create account
                            </LoadingButton>
                        </Box>
                    </Form>
                )}
            </Formik>
        </Container>
    );
};

export default Signup;
